'use client';

import { useSyncExternalStore } from 'react';

/**
 * The page clock, and the only way a client component may read the time during
 * render: `Date.now()` runs once on the server and again at hydration with a
 * different answer, and React throws the server's HTML away over the disagreement.
 */
const TICK_MS = 30_000;

let clockNow = 0;
const listeners = new Set<() => void>();
let timer: ReturnType<typeof setInterval> | null = null;

function subscribe(onChange: () => void): () => void {
  listeners.add(onChange);
  // One interval however many cards read it, so every countdown on screen flips
  // on the same tick instead of drifting apart. Starting it is also the moment
  // to re-read the clock: with nobody subscribed nothing was ticking, so the
  // cached value is as old as the gap since the last card unmounted.
  if (timer === null) {
    clockNow = Date.now();
    timer = setInterval(() => {
      clockNow = Date.now();
      for (const listener of listeners) listener();
    }, TICK_MS);
  }
  return () => {
    listeners.delete(onChange);
    if (listeners.size === 0 && timer !== null) {
      clearInterval(timer);
      timer = null;
    }
  };
}

function getSnapshot(): number {
  if (clockNow === 0) clockNow = Date.now();
  return clockNow;
}

/** Zero, never a clock: React uses this for the SSR render AND the hydration
 *  render, so both must land on `serverNow` and agree. */
function getServerSnapshot(): number {
  return 0;
}

export function useClock(serverNow: number): number {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot) || serverNow;
}
